import React, { createContext, useCallback, useContext, useReducer } from "react";
import { findEntry } from "./registry";

const WindowManagerCtx = createContext(null);

function highestZ(state) {
  return Object.values(state).reduce((max, w) => Math.max(max, w.zIndex), 0);
}

function buildInitialState([windowIds, openByDefault]) {
  const state = {};
  let z = 1;
  for (const id of windowIds) {
    const entry = findEntry(id);
    if (!entry) continue;
    const isOpen = openByDefault.includes(id);
    state[id] = {
      ...entry.defaultRect,
      status: isOpen ? "open" : "closed",
      zIndex: isOpen ? z++ : 0,
      maximized: false,
      title: null,
    };
  }
  return state;
}

function reducer(state, action) {
  if (action.type === "SET_RECTS") {
    const next = { ...state };
    for (const [id, rect] of Object.entries(action.rects)) {
      if (next[id]) next[id] = { ...next[id], ...rect };
    }
    return next;
  }

  const current = state[action.id];
  if (!current) return state;

  switch (action.type) {
    case "OPEN":
      return { ...state, [action.id]: { ...current, status: "open", zIndex: highestZ(state) + 1 } };
    case "CLOSE":
      return { ...state, [action.id]: { ...current, status: "closed", maximized: false } };
    case "MINIMIZE":
      return { ...state, [action.id]: { ...current, status: "minimized" } };
    case "FOCUS": {
      const top = highestZ(state);
      if (current.zIndex === top && current.status === "open") return state;
      return { ...state, [action.id]: { ...current, zIndex: top + 1 } };
    }
    case "MOVE":
      return { ...state, [action.id]: { ...current, x: action.x, y: action.y } };
    case "RESIZE":
      return { ...state, [action.id]: { ...current, width: action.width, height: action.height } };
    case "TOGGLE_MAX":
      return { ...state, [action.id]: { ...current, maximized: !current.maximized, zIndex: highestZ(state) + 1 } };
    case "SET_MAX":
      if (current.maximized === action.value) return state;
      return { ...state, [action.id]: { ...current, maximized: action.value, zIndex: highestZ(state) + 1 } };
    case "SET_TITLE":
      if (current.title === action.title) return state;
      return { ...state, [action.id]: { ...current, title: action.title } };
    default:
      return state;
  }
}

export function WindowManagerProvider({ children, windowIds, openByDefault }) {
  const [windows, dispatch] = useReducer(reducer, [windowIds, openByDefault], buildInitialState);

  const openWindow = useCallback((id) => dispatch({ type: "OPEN", id }), []);
  const closeWindow = useCallback((id) => dispatch({ type: "CLOSE", id }), []);
  const minimizeWindow = useCallback((id) => dispatch({ type: "MINIMIZE", id }), []);
  const focusWindow = useCallback((id) => dispatch({ type: "FOCUS", id }), []);
  const moveWindow = useCallback((id, x, y) => dispatch({ type: "MOVE", id, x, y }), []);
  const resizeWindow = useCallback((id, width, height) => dispatch({ type: "RESIZE", id, width, height }), []);
  const toggleMaximize = useCallback((id) => dispatch({ type: "TOGGLE_MAX", id }), []);
  const setMaximized = useCallback((id, value) => dispatch({ type: "SET_MAX", id, value }), []);
  const setTitle = useCallback((id, title) => dispatch({ type: "SET_TITLE", id, title }), []);
  const setRects = useCallback((rects) => dispatch({ type: "SET_RECTS", rects }), []);

  const value = {
    windows,
    openWindow,
    closeWindow,
    minimizeWindow,
    focusWindow,
    moveWindow,
    resizeWindow,
    toggleMaximize,
    setMaximized,
    setTitle,
    setRects,
  };

  return <WindowManagerCtx.Provider value={value}>{children}</WindowManagerCtx.Provider>;
}

export function useWindowManager() {
  const ctx = useContext(WindowManagerCtx);
  if (!ctx) throw new Error("useWindowManager must be used within a WindowManagerProvider");
  return ctx;
}
